import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { getModule, getLiveClasses, getMyEnrollments, getCourseRecordings, getModuleProgress, markProgress, checkCertificateEligibility, generateCertificate, getDocumentUrl, getMyGrades, getMyAttendance, getSubjectDocuments, formatApiError } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Progress } from '../../components/ui/progress';
import { ArrowLeft, BookOpen, Video, FileText, Calendar, Clock, Users, Download, Loader2, Check, GraduationCap, Film, Play, ExternalLink, CheckCircle2, Lock, Unlock, Award, CheckSquare, Square, Copy, Landmark, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';

export default function CourseDetail() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [module, setModule] = useState(null);
  const [recordings, setRecordings] = useState([]);
  const [moduleProgress, setModuleProgress] = useState(null);
  const [certificateStatus, setCertificateStatus] = useState(null);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showEnrollDialog, setShowEnrollDialog] = useState(false);
  const [subscriptionType, setSubscriptionType] = useState('monthly');
  const [enrolling, setEnrolling] = useState(false);
  const [error, setError] = useState('');
  const [expandedSubject, setExpandedSubject] = useState(null);
  const [liveClasses, setLiveClasses] = useState([]);
  const [subjectGrades, setSubjectGrades] = useState({});
  const [subjectAttendance, setSubjectAttendance] = useState({});
  const [subjectDocs, setSubjectDocs] = useState({});

  const fetchData = async () => {
    try {
      const [moduleData, enrollmentsData] = await Promise.all([
        getModule(courseId), getMyEnrollments()
      ]);
      setModule(moduleData);
      const enrolled = enrollmentsData.some(e => e.course_id === courseId);
      setIsEnrolled(enrolled);
      if (enrolled) {
        try {
          const [recs, prog, cert, classes] = await Promise.all([
            getCourseRecordings(courseId), getModuleProgress(courseId), checkCertificateEligibility(courseId),
            getLiveClasses(courseId)
          ]);
          setRecordings(recs);
          setModuleProgress(prog);
          setCertificateStatus(cert);
          setLiveClasses(classes);
        } catch (err) { console.log('No progress yet'); }
      }
    } catch (err) { console.error(err); toast.error('Error al cargar'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [courseId]);

  const handleEnroll = () => {
    setShowEnrollDialog(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Copiado al portapapeles');
    }).catch(() => {
      toast.error('No se pudo copiar');
    });
  };

  const handleMarkComplete = async (recordingId, isCompleted) => {
    try {
      await markProgress(recordingId, 'recording', !isCompleted);
      const [prog, cert] = await Promise.all([getModuleProgress(courseId), checkCertificateEligibility(courseId)]);
      setModuleProgress(prog);
      setCertificateStatus(cert);
      toast.success(isCompleted ? 'Marcado como pendiente' : 'Clase completada!');
    } catch (err) { toast.error('Error al actualizar progreso'); }
  };

  const handleGenerateCertificate = async () => {
    try {
      const result = await generateCertificate(courseId);
      toast.success('Certificado generado!');
      navigate(`/certificate/${result.certificate_id}`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const getRecordingsForSubject = (subjectId) => recordings.filter(r => r.subject_id === subjectId);

  const getLiveClassesForSubject = (subjectId) => liveClasses.filter(c => c.subject_id === subjectId && !c.recording_url);

  const getUpcomingClassesForSubject = (subjectId) => {
    const now = new Date();
    return getLiveClassesForSubject(subjectId).filter(c => new Date(c.scheduled_at) > new Date(now.getTime() - 60 * 60 * 1000));
  };

  const isRecordingCompleted = (recordingId) => {
    if (!moduleProgress?.subjects) return false;
    return moduleProgress.subjects.some(s => s.completed_classes?.includes(recordingId));
  };

  const getSubjectProgress = (subjectId) => moduleProgress?.subjects?.find(s => s.subject_id === subjectId);

  if (loading) return <Layout><div className="p-6 md:p-8 flex items-center justify-center min-h-[60vh]"><div className="spinner" /></div></Layout>;
  if (!module) return <Layout><div className="p-6 text-center py-16"><BookOpen className="h-16 w-16 mx-auto text-[#D9B48F] mb-4" /><h2 className="font-heading text-xl text-[#1A1A1A]">Modulo no encontrado</h2></div></Layout>;

  const savingsPercent = Math.round(100 - (module.price_annual / (module.price_monthly * (module.duration_months || 6))) * 100);

  return (
    <Layout>
      <div className="p-6 md:p-8" data-testid="course-detail-page">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6 text-[#4A4A4A] hover:text-[#1A1A1A] hover:bg-[#F5F2EA]">
          <ArrowLeft className="h-4 w-4 mr-2" /> Volver
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          <div className="lg:col-span-2">
            <div className="h-64 md:h-80 bg-gradient-to-br from-[#8B1538] to-[#70102B] rounded-lg flex items-center justify-center relative overflow-hidden">
              <div className="text-center text-white p-8">
                <GraduationCap className="h-20 w-20 mx-auto mb-4 text-white/50" />
                <h2 className="font-heading text-2xl md:text-3xl">{module.title}</h2>
                <p className="text-white/70 mt-2">{module.subjects_count} cursos | {module.duration_months || 6} meses</p>
              </div>
              {isEnrolled && <div className="absolute top-4 right-4 bg-[#2D6A4F] text-white px-4 py-2 rounded-full font-medium flex items-center gap-2"><Check className="h-4 w-4" />Inscrito</div>}
            </div>
          </div>
          <Card className="bg-white border border-[#E5DFD3] shadow-sm h-fit">
            <CardContent className="pt-6">
              <h1 className="font-heading text-2xl font-medium text-[#1A1A1A] mb-2">{module.title}</h1>
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3 text-[#4A4A4A]"><Users className="h-5 w-5 text-[#8B1538]" /><span>{module.enrolled_count} inscritos</span></div>
                <div className="flex items-center gap-3 text-[#4A4A4A]"><BookOpen className="h-5 w-5 text-[#8B1538]" /><span>{module.subjects_count} cursos</span></div>
                <div className="flex items-center gap-3 text-[#4A4A4A]"><FileText className="h-5 w-5 text-[#8B1538]" /><span>{module.documents?.length || 0} documentos</span></div>
              </div>
              <div className="border-t border-[#E5DFD3] pt-4">
                <div className="flex items-baseline justify-between mb-2">
                  <div><span className="text-sm text-[#737373]">Mensualidad</span><p className="text-2xl font-bold text-[#8B1538]">${module.price_monthly.toLocaleString()}</p></div>
                  <div className="text-right"><span className="text-sm text-[#737373]">Pago Anticipado</span><p className="text-xl font-bold text-[#1A1A1A]">${module.price_annual.toLocaleString()}</p>{savingsPercent > 0 && <span className="text-xs text-[#2D6A4F] font-medium">Ahorra {savingsPercent}%</span>}</div>
                </div>
                {isEnrolled ? (
                  <Button data-testid="access-module-btn" className="w-full bg-[#2D6A4F] hover:bg-[#1B4332] text-white mt-4" onClick={() => document.getElementById('subjects-section')?.scrollIntoView({ behavior: 'smooth' })}>Ver Cursos</Button>
                ) : (
                  <Button data-testid="enroll-module-btn" onClick={() => setShowEnrollDialog(true)} className="w-full bg-[#8B1538] hover:bg-[#70102B] text-white mt-4"><Landmark className="h-4 w-4 mr-2" />Inscribirse Ahora</Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Module Description */}
        <Card className="bg-white border border-[#E5DFD3] shadow-sm mb-8">
          <CardHeader><CardTitle className="font-heading text-xl text-[#1A1A1A]">Descripcion del Programa</CardTitle></CardHeader>
          <CardContent><p className="text-[#4A4A4A] leading-relaxed whitespace-pre-line">{module.description}</p></CardContent>
        </Card>

        {/* Certificate Section */}
        {isEnrolled && certificateStatus && (
          <Card className={`border shadow-sm mb-8 ${certificateStatus.eligible ? 'bg-gradient-to-r from-[#8B1538]/5 to-[#D9B48F]/10 border-[#8B1538]/30' : 'bg-white border-[#E5DFD3]'}`}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`h-14 w-14 rounded-full flex items-center justify-center ${certificateStatus.eligible ? 'bg-[#8B1538]/10' : 'bg-[#E5DFD3]'}`}>
                    <Award className={`h-7 w-7 ${certificateStatus.eligible ? 'text-[#8B1538]' : 'text-[#737373]'}`} />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg text-[#1A1A1A]">{certificateStatus.eligible ? 'Felicitaciones! Has completado el modulo' : 'Certificado de Finalizacion'}</h3>
                    <p className="text-sm text-[#737373]">{certificateStatus.eligible ? (certificateStatus.has_certificate ? 'Certificado emitido' : 'Ya puedes obtener tu certificado') : `Completa ${100 - (certificateStatus.progress_percent || 0)}% restante`}</p>
                  </div>
                </div>
                {certificateStatus.eligible && (
                  <Button data-testid="certificate-btn" onClick={certificateStatus.has_certificate ? () => navigate(`/certificate/${certificateStatus.certificate_id}`) : handleGenerateCertificate} className="bg-[#8B1538] hover:bg-[#70102B] text-white">
                    <Award className="h-4 w-4 mr-2" />{certificateStatus.has_certificate ? 'Ver Certificado' : 'Obtener Certificado'}
                  </Button>
                )}
              </div>
              {!certificateStatus.eligible && (
                <div className="mt-4">
                  <div className="flex justify-between text-sm mb-1"><span className="text-[#737373]">Progreso hacia el certificado</span><span className="font-medium text-[#8B1538]">{certificateStatus.progress_percent || 0}%</span></div>
                  <Progress value={certificateStatus.progress_percent || 0} className="h-2" />
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Overall Progress */}
        {isEnrolled && moduleProgress && (
          <Card className="bg-white border border-[#E5DFD3] shadow-sm mb-8">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-xl text-[#1A1A1A]">Progreso General</CardTitle>
                <span className="text-lg font-bold text-[#8B1538]">{moduleProgress.overall_percent}%</span>
              </div>
            </CardHeader>
            <CardContent><Progress value={moduleProgress.overall_percent} className="h-3" /></CardContent>
          </Card>
        )}

        {/* Subjects (Courses) - Sequential */}
        <div id="subjects-section" className="mb-8">
          <h2 className="font-heading text-xl text-[#1A1A1A] mb-6 flex items-center gap-2"><BookOpen className="h-5 w-5 text-[#8B1538]" /> Cursos del Programa</h2>
          {module.subjects?.length > 0 ? (
            <div className="space-y-4">
              {module.subjects.map((sub, idx) => {
                const subProgress = getSubjectProgress(sub.id);
                const isUnlocked = !isEnrolled || !subProgress ? (idx === 0 || !isEnrolled) : subProgress.unlocked;
                const subRecordings = getRecordingsForSubject(sub.id);
                const isExpanded = expandedSubject === sub.id;
                const percent = subProgress?.progress_percent || 0;
                const isComplete = percent >= 100;

                return (
                  <Card key={sub.id} data-testid={`subject-card-${sub.id}`} className={`border shadow-sm transition-all ${isComplete ? 'bg-[#2D6A4F]/5 border-[#2D6A4F]/30' : isUnlocked ? 'bg-white border-[#E5DFD3]' : 'bg-[#F5F2EA] border-[#E5DFD3] opacity-70'}`}>
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-4 cursor-pointer" onClick={async () => {
                        if (!isUnlocked || !isEnrolled) return;
                        const newId = isExpanded ? null : sub.id;
                        setExpandedSubject(newId);
                        if (newId && !subjectGrades[newId]) {
                          try {
                            const [g, a, d] = await Promise.all([getMyGrades(newId), getMyAttendance(newId), getSubjectDocuments(newId)]);
                            setSubjectGrades(prev => ({ ...prev, [newId]: g }));
                            setSubjectAttendance(prev => ({ ...prev, [newId]: a }));
                            setSubjectDocs(prev => ({ ...prev, [newId]: d }));
                          } catch (e) { /* no data yet */ }
                        }
                      }}>
                        <div className={`h-12 w-12 rounded-full flex items-center justify-center flex-shrink-0 ${isComplete ? 'bg-[#2D6A4F] text-white' : isUnlocked ? 'bg-[#8B1538] text-white' : 'bg-[#E5DFD3] text-[#737373]'}`}>
                          {isComplete ? <CheckCircle2 className="h-6 w-6" /> : isUnlocked ? <span className="font-bold">{sub.order}</span> : <Lock className="h-5 w-5" />}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className={`font-heading text-lg ${isComplete ? 'text-[#2D6A4F]' : 'text-[#1A1A1A]'}`}>{sub.title}</h3>
                              <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-[#737373]">
                                <span>Docente: <span className="font-medium text-[#B8860B]">{sub.teacher_name}</span></span>
                                <span className="flex items-center gap-0.5"><Calendar className="h-3.5 w-3.5" /> {sub.duration_weeks} sem.</span>
                                <span className="flex items-center gap-0.5"><Clock className="h-3.5 w-3.5" /> {sub.classes_per_week} clase/sem</span>
                              </div>
                            </div>
                            {isEnrolled && isUnlocked && (
                              <div className="text-right">
                                <span className={`text-sm font-semibold ${isComplete ? 'text-[#2D6A4F]' : 'text-[#8B1538]'}`}>{percent}%</span>
                                <div className="w-24 mt-1"><Progress value={percent} className="h-1.5" /></div>
                              </div>
                            )}
                            {!isUnlocked && <span className="text-xs bg-[#E5DFD3] text-[#737373] px-2 py-1 rounded-full">Bloqueado</span>}
                          </div>
                          {sub.description && <p className="text-sm text-[#4A4A4A] mt-2">{sub.description}</p>}
                        </div>
                      </div>

                      {/* Expanded: Show live classes + recordings for this subject */}
                      {isExpanded && isEnrolled && isUnlocked && (() => {
                        const upcomingClasses = getUpcomingClassesForSubject(sub.id);
                        return (
                          <div className="mt-4 ml-16 space-y-4">
                            {/* Upcoming Live Classes */}
                            {upcomingClasses.length > 0 && (
                              <div className="space-y-3">
                                <h4 className="text-sm font-semibold text-[#8B1538] uppercase tracking-wide flex items-center gap-1"><Video className="h-3.5 w-3.5" /> Clases en Vivo</h4>
                                {upcomingClasses.map((cls) => {
                                  const isNow = new Date(cls.scheduled_at) <= new Date() && new Date(cls.scheduled_at) > new Date(Date.now() - cls.duration_minutes * 60 * 1000);
                                  const isFuture = new Date(cls.scheduled_at) > new Date();
                                  return (
                                    <div key={cls.id} data-testid={`live-class-${cls.id}`} className={`flex items-center justify-between p-3 rounded-md ${isNow ? 'bg-[#2D6A4F]/10 border border-[#2D6A4F]/30' : 'bg-[#8B1538]/5 border border-[#8B1538]/10'}`}>
                                      <div className="flex items-center gap-3">
                                        <div className={`h-10 w-10 rounded-full flex items-center justify-center ${isNow ? 'bg-[#2D6A4F] animate-pulse' : 'bg-[#8B1538]/20'}`}>
                                          <Video className={`h-5 w-5 ${isNow ? 'text-white' : 'text-[#8B1538]'}`} />
                                        </div>
                                        <div>
                                          <p className="text-sm font-medium text-[#1A1A1A]">{cls.title}</p>
                                          <p className="text-xs text-[#737373]">
                                            {new Date(cls.scheduled_at).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' })} - {new Date(cls.scheduled_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })} — {cls.duration_minutes} min
                                          </p>
                                          {isNow && <span className="text-xs text-[#2D6A4F] font-semibold">EN VIVO AHORA</span>}
                                        </div>
                                      </div>
                                      <Button data-testid={`join-class-btn-${cls.id}`} size="sm" onClick={() => navigate(`/student/class/${cls.id}`)} className={`${isNow ? 'bg-[#2D6A4F] hover:bg-[#1B4332]' : 'bg-[#8B1538] hover:bg-[#70102B]'} text-white`}>
                                        <Video className="h-4 w-4 mr-1" /> {isNow ? 'Unirse Ahora' : 'Unirse'}
                                      </Button>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {/* Recordings */}
                            {subRecordings.length > 0 && (
                              <div className="space-y-3">
                                <h4 className="text-sm font-semibold text-[#737373] uppercase tracking-wide flex items-center gap-1"><Film className="h-3.5 w-3.5" /> Grabaciones</h4>
                                {subRecordings.map((rec) => {
                                  const completed = isRecordingCompleted(rec.id);
                                  return (
                                    <div key={rec.id} data-testid={`recording-${rec.id}`} className={`flex items-center justify-between p-3 rounded-md ${completed ? 'bg-[#2D6A4F]/5 border border-[#2D6A4F]/20' : 'bg-[#F5F2EA]'}`}>
                                      <div className="flex items-center gap-3">
                                        {completed ? <CheckCircle2 className="h-5 w-5 text-[#2D6A4F]" /> : <Play className="h-5 w-5 text-[#8B1538]" />}
                                        <div>
                                          <p className={`text-sm font-medium ${completed ? 'text-[#2D6A4F]' : 'text-[#1A1A1A]'}`}>{rec.title}</p>
                                          <p className="text-xs text-[#737373]">{new Date(rec.scheduled_at).toLocaleDateString('es-ES')} — {rec.duration_minutes} min</p>
                                        </div>
                                      </div>
                                      <div className="flex gap-2">
                                        <a href={rec.recording_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#8B1538] hover:bg-[#70102B] text-white rounded text-xs font-medium"><ExternalLink className="h-3 w-3" /> Ver</a>
                                        <button onClick={() => handleMarkComplete(rec.id, completed)} className={`px-2 py-1.5 rounded text-xs ${completed ? 'bg-[#2D6A4F]/10 text-[#2D6A4F]' : 'bg-[#E5DFD3] text-[#4A4A4A]'}`}>
                                          {completed ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {upcomingClasses.length === 0 && subRecordings.length === 0 && (
                              <div className="text-sm text-[#737373] italic">Aun no hay clases ni grabaciones para este curso.</div>
                            )}

                            {/* Student Grades */}
                            {subjectGrades[sub.id]?.grades?.length > 0 && (
                              <div className="space-y-2">
                                <h4 className="text-sm font-semibold text-[#8B1538] uppercase tracking-wide flex items-center gap-1"><ClipboardList className="h-3.5 w-3.5" /> Mis Notas</h4>
                                <div className="bg-[#F5F2EA] rounded-md p-3 space-y-2">
                                  {subjectGrades[sub.id].grades.map(g => (
                                    <div key={g.column_id} className="flex items-center justify-between text-sm">
                                      <span className="text-[#4A4A4A]">{g.title}</span>
                                      <span className={`font-semibold ${g.value === null ? 'text-[#737373]' : g.value >= 5.0 ? 'text-[#2D6A4F]' : g.value >= 4.0 ? 'text-[#B8860B]' : 'text-[#9B2226]'}`}>
                                        {g.value !== null ? g.value.toFixed(1) : 'Sin nota'}
                                      </span>
                                    </div>
                                  ))}
                                  {subjectGrades[sub.id].average !== null && (
                                    <div className="flex items-center justify-between text-sm border-t border-[#E5DFD3] pt-2 mt-2">
                                      <span className="font-semibold text-[#1A1A1A]">Promedio</span>
                                      <span className={`text-base font-bold ${subjectGrades[sub.id].average >= 5.0 ? 'text-[#2D6A4F]' : subjectGrades[sub.id].average >= 4.0 ? 'text-[#B8860B]' : 'text-[#9B2226]'}`}>
                                        {subjectGrades[sub.id].average.toFixed(1)}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Student Attendance */}
                            {subjectAttendance[sub.id]?.total > 0 && (
                              <div className="space-y-2">
                                <h4 className="text-sm font-semibold text-[#737373] uppercase tracking-wide flex items-center gap-1"><Users className="h-3.5 w-3.5" /> Asistencia</h4>
                                <div className="bg-[#F5F2EA] rounded-md p-3">
                                  <div className="flex items-center justify-between text-sm mb-2">
                                    <span className="text-[#4A4A4A]">Asistencia general</span>
                                    <span className={`font-bold ${subjectAttendance[sub.id].attendance_percent >= 75 ? 'text-[#2D6A4F]' : 'text-[#9B2226]'}`}>
                                      {subjectAttendance[sub.id].attendance_percent}% ({subjectAttendance[sub.id].present}/{subjectAttendance[sub.id].total})
                                    </span>
                                  </div>
                                  <Progress value={subjectAttendance[sub.id].attendance_percent} className="h-2" />
                                </div>
                              </div>
                            )}

                            {/* Subject Documents */}
                            {subjectDocs[sub.id]?.length > 0 && (
                              <div className="space-y-2">
                                <h4 className="text-sm font-semibold text-[#8B1538] uppercase tracking-wide flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> Material del Curso</h4>
                                <div className="space-y-2">
                                  {subjectDocs[sub.id].map(doc => (
                                    <div key={doc.id} className="flex items-center justify-between p-2.5 bg-[#F5F2EA] rounded-md">
                                      <div className="flex items-center gap-2.5">
                                        <FileText className="h-4 w-4 text-[#8B1538]" />
                                        <div>
                                          <p className="text-sm font-medium text-[#1A1A1A]">{doc.filename}</p>
                                          <p className="text-[10px] text-[#737373]">{doc.uploaded_by_name} — {new Date(doc.uploaded_at).toLocaleDateString('es-ES')}</p>
                                        </div>
                                      </div>
                                      <a href={getDocumentUrl(doc.storage_path)} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded hover:bg-[#8B1538]/10">
                                        <Download className="h-4 w-4 text-[#8B1538]" />
                                      </a>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 bg-[#F5F2EA] rounded-lg"><BookOpen className="h-12 w-12 mx-auto text-[#D9B48F] mb-3" /><p className="text-[#737373]">Los cursos se irán agregando</p></div>
          )}
        </div>

        {/* Documents */}
        {module.documents?.length > 0 && (
          <Card className="bg-white border border-[#E5DFD3] shadow-sm mb-8">
            <CardHeader><CardTitle className="font-heading text-xl text-[#1A1A1A] flex items-center gap-2"><FileText className="h-5 w-5 text-[#8B1538]" /> Material de Estudio</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                {module.documents.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-3 bg-[#F5F2EA] rounded-md">
                    <div className="flex items-center gap-3"><div className="h-10 w-10 rounded-md bg-[#8B1538]/10 flex items-center justify-center"><FileText className="h-5 w-5 text-[#8B1538]" /></div><div><p className="font-medium text-[#1A1A1A] text-sm">{doc.filename}</p><p className="text-xs text-[#737373]">{(doc.size / 1024).toFixed(1)} KB</p></div></div>
                    {isEnrolled && <a href={getDocumentUrl(doc.storage_path)} target="_blank" rel="noopener noreferrer"><Button variant="ghost" size="sm" className="text-[#8B1538]"><Download className="h-4 w-4" /></Button></a>}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Enrollment Dialog - Transfer Payment */}
        <Dialog open={showEnrollDialog} onOpenChange={setShowEnrollDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Inscribirse en {module?.title}</DialogTitle>
              <DialogDescription>Realiza una transferencia bancaria y el administrador activara tu inscripcion</DialogDescription>
            </DialogHeader>

            {/* Plan selection */}
            <RadioGroup value={subscriptionType} onValueChange={setSubscriptionType} className="space-y-3">
              <div className={`flex items-center justify-between p-3 border rounded-md cursor-pointer transition-colors ${subscriptionType === 'monthly' ? 'border-[#8B1538] bg-[#8B1538]/5' : 'border-[#E5DFD3]'}`}>
                <div className="flex items-center gap-3"><RadioGroupItem value="monthly" id="m" /><Label htmlFor="m" className="cursor-pointer"><span className="font-medium text-[#1A1A1A]">Mensualidad</span><p className="text-xs text-[#737373]">Pago cada mes</p></Label></div>
                <span className="font-bold text-[#8B1538]">${module?.price_monthly?.toLocaleString()}/mes</span>
              </div>
              <div className={`flex items-center justify-between p-3 border rounded-md cursor-pointer transition-colors ${subscriptionType === 'annual' ? 'border-[#8B1538] bg-[#8B1538]/5' : 'border-[#E5DFD3]'}`}>
                <div className="flex items-center gap-3"><RadioGroupItem value="annual" id="a" /><Label htmlFor="a" className="cursor-pointer"><span className="font-medium text-[#1A1A1A]">Pago Anticipado</span><p className="text-xs text-[#737373]">Pago unico con descuento</p></Label></div>
                <div className="text-right"><span className="font-bold text-[#8B1538]">${module?.price_annual?.toLocaleString()}</span>{savingsPercent > 0 && <p className="text-xs text-[#2D6A4F]">Ahorra {savingsPercent}%</p>}</div>
              </div>
            </RadioGroup>

            {/* Transfer details */}
            <div className="bg-[#F5F2EA] rounded-lg p-4 space-y-3 border border-[#E5DFD3]">
              <div className="flex items-center gap-2 mb-1">
                <Landmark className="h-5 w-5 text-[#8B1538]" />
                <h3 className="font-heading text-base font-medium text-[#1A1A1A]">Datos para Transferencia</h3>
              </div>
              {[
                { label: 'Banco', value: 'Mercado Pago' },
                { label: 'Tipo de cuenta', value: 'Cuenta Vista' },
                { label: 'N de cuenta', value: '1006115882' },
                { label: 'Nombre', value: 'Nicole Scarleth Carrasco Arriagada' },
                { label: 'RUT', value: '20.364.315-2' },
                { label: 'Monto', value: `$${subscriptionType === 'annual' ? module?.price_annual?.toLocaleString() : module?.price_monthly?.toLocaleString()} CLP` },
              ].map((item) => (
                <div key={item.label} className="flex items-center justify-between text-sm">
                  <span className="text-[#737373]">{item.label}:</span>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-[#1A1A1A]">{item.value}</span>
                    <button
                      onClick={() => copyToClipboard(item.value)}
                      className="p-1 rounded hover:bg-[#E5DFD3] transition-colors"
                      title="Copiar"
                    >
                      <Copy className="h-3.5 w-3.5 text-[#737373]" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <Alert className="bg-[#8B1538]/5 border-[#8B1538]/20">
              <AlertDescription className="text-sm text-[#4A4A4A]">
                Una vez realizada la transferencia, el administrador verificara tu pago y activara tu inscripcion. Si tienes dudas, contactanos.
              </AlertDescription>
            </Alert>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEnrollDialog(false)} className="border-[#E5DFD3]">Cerrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
